'use client';

import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { buildSnowplowPageViewRouteKeyFromLocation } from './snowplowPageViewRoute';

interface BrowserLocationSnapshot {
  pathname: string;
  search: string;
}

/**
 * 读取当前浏览器 URL 快照，确保 pathname 与 search 来自同一次 location 状态。
 */
function getBrowserLocationSnapshot(): BrowserLocationSnapshot | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

/**
 * 在首屏和客户端路由变化时触发 Snowplow SDK 原生 page view。
 */
export function useSnowplowPageViewTracking(): void {
  const pathname = usePathname();
  const { trackEvent } = useReporting();
  const lastTrackedRouteKeyRef = useRef<string | null>(null);

  const trackCurrentPageView = useCallback(() => {
    const routeKey = buildSnowplowPageViewRouteKeyFromLocation(
      pathname,
      getBrowserLocationSnapshot(),
    );
    if (!routeKey || lastTrackedRouteKeyRef.current === routeKey) {
      return;
    }

    lastTrackedRouteKeyRef.current = routeKey;
    trackEvent({ eventName: 'page_view' }, [ProviderType.SNOWPLOW]);
  }, [pathname, trackEvent]);

  useEffect(() => {
    trackCurrentPageView();
  }, [trackCurrentPageView]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('popstate', trackCurrentPageView);
    window.addEventListener('zl-history-change', trackCurrentPageView);

    return () => {
      window.removeEventListener('popstate', trackCurrentPageView);
      window.removeEventListener('zl-history-change', trackCurrentPageView);
    };
  }, [trackCurrentPageView]);
}
